import get from 'lodash/get'
import {
  ICreateShipmentInput,
  ICreateShipmentStopAppointmentInput,
  ICreateShipmentStopInput,
  IEquipmentAttributeTypes,
  IEquipmentTypes,
  IShipmentEquipmentAttributeInput,
  IShipmentStopAppointmentStatuses,
  IShipperLoadTypes,
} from 'types/graphqlTypes'
import { TCreateShipmentSchema } from '../schema'
import { TStopSchema } from '../schema/ShipmentSchema/StopSchema'

/**
 * Transforms an object of type TStopSchema
 * to an object of type ICreateShipmentStopInput
 */
export const getStopData = (stop: TStopSchema): ICreateShipmentStopInput => {
  const {
    appointmentReference,
    appointmentType,
    billOfLadingReference,
    commodity,
    confirmationReference,
    confirmed,
    dropTrailer,
    endAtDate,
    endAtTime,
    genericCommodityId,
    palletCount,
    poReference,
    shipperFacility,
    specialInstruction,
    startAtDate,
    startAtTime,
    stopSequence,
    stopType,
    vendorId,
    weight,
  } = stop || {}

  const { id: shipperFacilityUuid = '' } = shipperFacility || {}

  const appointmentInput: ICreateShipmentStopAppointmentInput = {
    appointmentReference,
    appointmentType,
    confirmationReference,
    endAtDate,
    endAtTime,
    startAtDate,
    startAtTime,
    status: confirmed
      ? IShipmentStopAppointmentStatuses.confirmed
      : IShipmentStopAppointmentStatuses.unconfirmed,
  }

  const stopInput: ICreateShipmentStopInput = {
    appointmentInput,
    billOfLadingReference,
    commodity,
    dropTrailer,
    genericCommodityId,
    palletCount,
    poReference,
    shipperFacilityUuid,
    specialInstruction,
    stopSequence,
    stopType,
    weight,
    vendorId,
  }
  return stopInput
}

/**
 * Returns an array of IShipmentEquipmentAttributes
 */
export const getEquipmentAttributesData = (
  attributes: object
): IShipmentEquipmentAttributeInput[] => {
  const keys = Object.keys(attributes)
  return keys
    .filter(key => {
      const value = get(attributes, key, '')
      return value
    })
    .map(key => ({
      type: key as IEquipmentAttributeTypes,
      value: String(get(attributes, key, '')),
    }))
}

/**
 * Returns an object of type ICreateShipmentInput
 * from form values of type TCreateShipmentSchema
 */
export const getCreateShipmentMutationInput = (
  formValues: TCreateShipmentSchema
): ICreateShipmentInput => {
  const { equipmentAttributes = {}, equipmentType, equipment, shipment, shipperApplication } =
    formValues || {}
  const { id: shipperApplicationId = '' } = shipperApplication || {}
  const {
    carrierSpecialInstructions,
    floorLoaded,
    hot,
    isRelatedShipment,
    opsRep,
    originalShipmentId,
    shipperMiles,
    shipperPerMileFuelRate,
    shipperRate,
    shipperRateType,
    stops,
    team,
    totalPallets,
    totalWeight,
  } = shipment || {}
  const { id: opsRepId = '' } = opsRep || {}

  /**
   * equipType equals the subtype selected in the form (`equipmentType`)
   * or 'specialized' which does not have any subtype options
   */
  const equipType =
    equipment === IEquipmentTypes.specialized ? IEquipmentTypes.specialized : equipmentType

  return {
    carrierSpecialInstructions,
    equipmentType: equipType,
    equipmentAttributes: getEquipmentAttributesData(equipmentAttributes),
    loadingType: floorLoaded ? IShipperLoadTypes.floor_load : null, // TODO(SOP-1657): Add other loading type fields
    hot,
    opsRepId,
    originalShipmentId: isRelatedShipment ? originalShipmentId : null,
    shipperApplicationId,
    shipperMiles,
    shipperPerMileFuelRate,
    shipperRate,
    shipperRateType,
    stops: stops.map(stop => getStopData(stop)),
    team,
    totalPalletCount: totalPallets,
    totalWeight,
  }
}
